import React, { useState, useRef } from 'react'
import PropTypes from 'prop-types'
import { Microphone, Pause, Play } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'

import { formatRecordTime } from '../../../utils'

const MAX_RECORDING_TIME = 60 // seconds

const RecordTab = ({ onChange = () => {}, value }) => {
  const { t } = useTranslation(['chat'])
  const [isRecording, setIsRecording] = useState(false)
  const [mediaRecorder, setMediaRecorder] = useState(null)
  const [recordingTime, setRecordingTime] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)
  const timerRef = useRef(null)
  const audioRef = useRef(null)
  const stopRef = useRef(null)

  const playAudio = () => {
    if (audioRef.current) {
      audioRef.current.play()
    }
  }

  const pauseAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause()
    }
  }

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const mediaRecorder = new MediaRecorder(stream)

      let chunks = []

      mediaRecorder.ondataavailable = (e) => {
        chunks.push(e.data)
      }
      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(chunks)
        onChange(audioBlob)

        const reader = new FileReader()
        reader.onload = () => {
          const dataUrl = reader.result
          if (audioRef.current) {
            audioRef.current.src = dataUrl.replace(
              'data:application/octet-stream',
              'data:audio/mp3',
            )
          }
        }
        reader.readAsDataURL(audioBlob)

        chunks = []

        // Clear audio timer
        clearInterval(timerRef.current)
        setRecordingTime(0)
      }
      mediaRecorder.start()
      setIsRecording(true)
      setMediaRecorder(mediaRecorder)

      // Start audio timer
      timerRef.current = setInterval(() => {
        setRecordingTime((prevTime) => prevTime + 1)
      }, 1000)

      // Stop recording after MAX_RECORDING_TIME seconds
      stopRef.current = setTimeout(() => {
        stopRecording()
      }, MAX_RECORDING_TIME * 1000)
    } catch ({ message }) {
      console.error('Error recording audio:', message)
    }
  }

  const stopRecording = () => {
    mediaRecorder.stop()
    setIsRecording(false)
    if (stopRef.current) {
      clearTimeout(stopRef.current)
    }
  }

  return (
    <div className="tab-content record-tab">
      {value ? (
        <React.Fragment>
          <span>{t('chat:manageVoicemail.recordSuccess')}</span>
          <audio
            ref={audioRef}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onEnded={() => setIsPlaying(false)}
          />
          <div>
            <button
              type="button"
              className="button"
              style={{ marginRight: 6 }}
              onClick={isPlaying ? pauseAudio : playAudio}
            >
              {isPlaying ? <Pause weight="fill" /> : <Play weight="fill" />}
            </button>
            <button type="button" className="button" onClick={() => onChange(null)}>
              {t('chat:manageVoicemail.recordNew')}
            </button>
          </div>
        </React.Fragment>
      ) : (
        <React.Fragment>
          <span>{t('chat:manageVoicemail.tapToRecord')}</span>
          <button
            type="button"
            className={`record-button ${isRecording ? 'is-recording' : ''}`}
            onClick={isRecording ? stopRecording : startRecording}
          >
            {isRecording ? <Pause weight="fill" /> : <Microphone weight="fill" />}
          </button>
          <span>
            {isRecording
              ? t('chat:manageVoicemail.recordingLabel', { time: formatRecordTime(recordingTime) })
              : ''}
          </span>
        </React.Fragment>
      )}
    </div>
  )
}

RecordTab.propTypes = {
  onChange: PropTypes.func,
  value: PropTypes.object,
}

export default RecordTab
